import { InvoiceUrlDto } from '@/services/platform/api/model';
import { getMeInvoiceUrlV1 } from '../../api/me-—-profile/me-—-profile';

/**
 * Outcome of an invoice URL request.
 *
 * Unlike most wrappers in this layer, failure is not collapsed into `undefined`: the caller has to
 * tell "the PDF is not ready yet" apart from "the request failed" to show the right message, and
 * the API models those as two different statuses (202 vs 401/404).
 */
export type InvoiceUrlResult =
  /** The PDF is ready. `sasUrl` is a short-lived (15 minute) link to blob storage. */
  | { kind: 'ready'; sasUrl: string; fileName?: string | null }
  /** The invoice exists but its PDF is still being generated — retrying later will succeed. */
  | { kind: 'pending' }
  /** Not found, not owned by this user, or the request failed outright. */
  | { kind: 'error' };

/**
 * Fetches a download URL for a single invoice belonging to the authenticated user.
 *
 * The API returns a 15-minute SAS URL rather than the PDF bytes, so the link must be opened
 * promptly and cannot be cached or shared. Ownership is enforced server-side via the
 * Order → Customer chain; invoices belonging to another user come back as 404.
 *
 * @param orderId - The order's public GUID (`OrderDetailDto.publicGuid`).
 * @param invoiceId - The invoice's public GUID (`InvoiceSummaryDto.publicGuid`).
 * @returns `{ kind: 'ready' }` with the SAS URL on HTTP 200.
 * @returns `{ kind: 'pending' }` on HTTP 202 — the invoice PDF is still being produced.
 * @returns `{ kind: 'error' }` on 401, 404, a 200 with no URL, or a network error.
 */
export async function getInvoiceUrl(orderId: string, invoiceId: string): Promise<InvoiceUrlResult> {
  try {
    const response = await getMeInvoiceUrlV1(orderId, invoiceId);
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      const { sasUrl, fileName } = (data ?? {}) as InvoiceUrlDto;

      // `sasUrl` is typed nullable, so a 200 is not on its own proof that there is a link to open.
      if (!sasUrl) {
        console.warn(`getInvoiceUrl: status 200 without a sasUrl for invoice "${invoiceId}"`);
        return { kind: 'error' };
      }

      return { kind: 'ready', sasUrl, fileName };
    }

    if (status === 202) {
      return { kind: 'pending' };
    }

    console.warn(`getInvoiceUrl: unexpected status ${status} for invoice "${invoiceId}"`);
    return { kind: 'error' };
  } catch (error) {
    console.warn(`getInvoiceUrl: request failed for invoice "${invoiceId}":`, error);
    return { kind: 'error' };
  }
}
